// @ts-check
/** @typedef {import('../types/api').Customer} Customer */
/** @typedef {import('../types/api').CustomersSummary} CustomersSummary */

import {
  ChevronDown,
  ChevronUp,
  DollarSign,
  Eye,
  RefreshCw,
  Search,
  ShoppingBag,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import TemplateActions from '../components/whatsapp/TemplateActions';
import { useToast } from '../context/ToastContext';
import adminCustomerService from '../services/adminCustomerService';
import api from '../utils/apiClient';

const CustomersPage = () => {
  const toast = useToast();
  /** @type {[Customer[], Function]} */
  const [customers, setCustomers] = useState([]);
  /** @type {[CustomersSummary | null, Function]} */
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialParams] = useSearchParams(); // /customers?search=<email> deep link from the WhatsApp inbox
  const [searchQuery, setSearchQuery] = useState(initialParams.get('search') || '');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const pageLimit = 50;

  // Get current admin user's role
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const adminRole = adminUser.role;

  const handleDeleteCustomer = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete customer "${name}"? This action is irreversible.`)) return;
    try {
      await api.del(`/api/admin/customers/${id}`);
      toast.success('Customer deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete customer');
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [customersData, summaryData] = await Promise.all([
        adminCustomerService.getCustomers({
          page: currentPage,
          limit: pageLimit,
          sort_by: sortBy,
          sort_order: sortOrder,
          search: searchQuery || undefined,
        }),
        adminCustomerService.getCustomersSummary(),
      ]);

      // Handle paginated response
      if (customersData.items) {
        setCustomers(customersData.items);
        setTotalPages(customersData.pages || 1);
        setTotalCustomers(customersData.total || 0);
      } else {
        const arr = /** @type {Customer[]} */ (/** @type {unknown} */ (customersData));
        setCustomers(arr);
        setTotalCustomers(arr.length);
      }

      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, currentPage, searchQuery, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = async () => {
    setCurrentPage(1); // Reset to page 1 on search
    loadData();
  };

  const handleViewDetails = async (customerId) => {
    try {
      const detail = await adminCustomerService.getCustomerDetail(customerId);
      setSelectedCustomer(detail);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load customer details:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredCustomers = searchQuery
    ? customers.filter(
        (c) =>
          c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.full_name && c.full_name.toLowerCase().includes(searchQuery.toLowerCase())),
      )
    : customers;

  if (loading && customers.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">Customer Management</h1>
          <p className="text-muted-foreground">View and manage your customer base</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card p-4 rounded-lg shadow-sm border">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <span className="text-muted-foreground text-sm">Total Customers</span>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold">{summary.total_customers}</div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm border">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <span className="text-muted-foreground text-sm">With Orders</span>
              <ShoppingBag className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold">{summary.customers_with_orders}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {((summary.customers_with_orders / summary.total_customers) * 100).toFixed(1)}% conversion
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm border">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <span className="text-muted-foreground text-sm">Total Revenue</span>
              <DollarSign className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold">
              {'\u20B9'}
              {Number(summary.total_revenue).toLocaleString('en-IN')}
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm border">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <span className="text-muted-foreground text-sm">Avg Lifetime Value</span>
              <TrendingUp className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-2xl font-bold">
              {'\u20B9'}
              {Number(summary.average_customer_lifetime_value).toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-card p-4 rounded-lg shadow-sm border mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by email or name..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                loadData();
              }}
              className="px-4 py-2 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-card rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Customer
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-accent"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Joined
                    {sortBy === 'created_at' &&
                      (sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />)}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-accent"
                  onClick={() => handleSort('total_orders')}
                >
                  <div className="flex items-center gap-1">
                    Orders
                    {sortBy === 'total_orders' &&
                      (sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />)}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-accent"
                  onClick={() => handleSort('total_spent')}
                >
                  <div className="flex items-center gap-1">
                    Total Spent
                    {sortBy === 'total_spent' &&
                      (sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />)}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Avg Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                    Loading customers...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                    No customers found
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-foreground">{customer.full_name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">{customer.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded-full">
                        {customer.stats.total_orders}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      {'\u20B9'}
                      {Number(customer.stats.total_spent).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {'\u20B9'}
                      {Number(customer.stats.average_order_value).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleViewDetails(customer.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        {adminRole === 'super_admin' && (
                          <button
                            onClick={() => handleDeleteCustomer(customer.id, customer.full_name || customer.email)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Detail Modal */}
      {showDetailModal && selectedCustomer && (
        <Modal maxWidth="4xl" closeOnBackdrop={false} className="rounded-lg shadow-xl">
          <div className="p-6 border-b flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{selectedCustomer.full_name || 'Customer Details'}</h2>
              <p className="text-muted-foreground">{selectedCustomer.email}</p>
            </div>
            <button
              onClick={() => setShowDetailModal(false)}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              ✕
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Customer Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm text-muted-foreground">Phone</label>
                <p className="font-medium">{selectedCustomer.phone || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Joined</label>
                <p className="font-medium">{new Date(selectedCustomer.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {selectedCustomer.phone && <TemplateActions scope="customers" id={selectedCustomer.id} className="mb-6" />}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Total Orders</div>
                <div className="text-2xl font-bold">{selectedCustomer.stats.total_orders}</div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Total Spent</div>
                <div className="text-2xl font-bold">
                  {'\u20B9'}
                  {Number(selectedCustomer.stats.total_spent).toLocaleString('en-IN')}
                </div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Avg Order</div>
                <div className="text-2xl font-bold">
                  {'\u20B9'}
                  {Number(selectedCustomer.stats.average_order_value).toLocaleString('en-IN')}
                </div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Last Order</div>
                <div className="text-sm font-medium">
                  {selectedCustomer.stats.last_order_date
                    ? new Date(selectedCustomer.stats.last_order_date).toLocaleDateString()
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Order History */}
            <div>
              <h3 className="text-lg font-bold mb-4">Order History</h3>
              {selectedCustomer.orders.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {selectedCustomer.orders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4 hover:bg-muted/50">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <span className="font-medium">{order.order_number}</span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            order.status === 'completed'
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                              : order.status === 'pending'
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                                : 'bg-muted text-foreground'
                          }`}
                        >
                          {order.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{new Date(order.created_at).toLocaleDateString()}</span>
                        <span>{order.items_count} items</span>
                        <span className="font-semibold text-foreground">
                          {'\u20B9'}
                          {Number(order.total_amount).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Previous
          </button>

          <span className="px-4 py-2 text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} ({totalCustomers} total)
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
